import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddDataExportRequest.css";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/data-export-requests/`;

const DEFAULT_SCOPE = `{
  "entities": ["projects", "inspections", "reports"],
  "date_range": {
    "from": "2024-01-01",
    "to": "2024-12-31"
  },
  "format": "csv"
}`;

const emptyRequest = {
  purpose: "",
  scope: DEFAULT_SCOPE
};

const AddDataExportRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editRequest = location.state?.editRequest || null;
  const isEditMode = Boolean(editRequest?.id);

  const [requests, setRequests] = useState(() => {
    if (editRequest) {
      return [
        {
          purpose: editRequest.purpose || "",
          scope:
            editRequest.scope !== null && editRequest.scope !== undefined
              ? JSON.stringify(editRequest.scope, null, 2)
              : DEFAULT_SCOPE
        }
      ];
    }

    return [{ ...emptyRequest }];
  });

  const [loading, setLoading] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setRequests((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [name]: value } : r))
    );
  };

  const addRequest = () => {
    if (isEditMode) return;
    setRequests((prev) => [...prev, { ...emptyRequest }]);
  };

  const removeRequest = (index) => {
    if (isEditMode || requests.length === 1) return;
    setRequests((prev) => prev.filter((_, i) => i !== index));
  };

  const resetScope = (index) => {
    setRequests((prev) =>
      prev.map((r, i) => (i === index ? { ...r, scope: DEFAULT_SCOPE } : r))
    );
  };

  const parseScope = (raw) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return {};
    const parsed = JSON.parse(trimmed);
    if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
      throw new Error("Scope must be a JSON object.");
    }
    return parsed;
  };

  const validateRow = (r, index) => {
    const label = `Request ${index + 1}`;

    if (!r.purpose.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please describe the purpose for ${label}.`
      });
      return false;
    }

    try {
      parseScope(r.scope);
    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Scope JSON",
        text: `Scope for ${label} must be a valid JSON object, e.g. {"entities": ["projects"], "format": "csv"}.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (r, tenantUser) => ({
    purpose: r.purpose.trim(),
    scope: parseScope(r.scope),
    tenant: tenantUser.tenant_id,
    user: tenantUser.user_id
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id || !tenantUser.user_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID or User ID is missing from tenant_user."
      });
      return;
    }

    for (let i = 0; i < requests.length; i++) {
      if (!validateRow(requests[i], i)) return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const payload = buildPayload(requests[0], tenantUser);

        console.log("Update Data Export Request Payload:", payload);

        const response = await axios.patch(
          `${API_URL}${editRequest.id}/`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("Update Data Export Request Response:", response.data);

        await Swal.fire({
          icon: "success",
          title: "Data Export Request Updated",
          text: "Data export request has been updated successfully.",
          confirmButtonText: "OK"
        });

        navigate("/admin/data-export-requests");
        return;
      }

      const payload = requests.map((r) => buildPayload(r, tenantUser));

      console.log("Create Data Export Requests Payload:", payload);

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" }
      });

      console.log("Create Data Export Requests Response:", response.data);

      await Swal.fire({
        icon: "success",
        title: "Data Export Requests Created",
        text: `${requests.length} data export request${
          requests.length > 1 ? "s" : ""
        } submitted successfully.`,
        confirmButtonText: "OK"
      });

      navigate("/admin/data-export-requests");
    } catch (error) {
      console.error(
        isEditMode
          ? "Update Data Export Request Error:"
          : "Create Data Export Requests Error:",
        error
      );

      let errorMessage = isEditMode
        ? "Unable to update data export request."
        : "Unable to submit data export requests.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          errorMessage = Object.entries(error.response.data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditMode ? "Update Failed" : "Submit Failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/data-export-requests");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="der-page">
          <div className="der-page-header">
            <div>
              <h2>
                {isEditMode
                  ? "Edit Data Export Request"
                  : "Create Data Export Requests"}
              </h2>
              <p>
                {isEditMode
                  ? "Update the details of this data export request."
                  : "Submit one or more formal requests to export tenant data."}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="der-card">
              <div className="der-form-body">
                {requests.map((r, index) => (
                  <div className="der-record" key={index}>
                    <div className="der-record-header">
                      <div>
                        <h4>
                          {isEditMode
                            ? "Request Details"
                            : `Request ${index + 1}`}
                        </h4>
                      </div>

                      {!isEditMode && requests.length > 1 && (
                        <button
                          type="button"
                          className="der-remove-btn"
                          onClick={() => removeRequest(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="der-form-grid">
                      {/* Purpose */}
                      <div className="der-form-group der-form-group--full">
                        <label>
                          Purpose <span>*</span>
                        </label>
                        <textarea
                          name="purpose"
                          value={r.purpose}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Describe why this data export is being requested (e.g. regulatory audit, migration, internal review)."
                          rows={4}
                          required
                        />
                        <p className="der-hint">
                          A clear purpose is required for the approval trail.
                        </p>
                      </div>

                      {/* Scope (JSON) */}
                      <div className="der-form-group der-form-group--full">
                        <div className="der-scope-header">
                          <label>
                            Scope (JSON) <span>*</span>
                          </label>
                          <button
                            type="button"
                            className="der-reset-btn"
                            onClick={() => resetScope(index)}
                          >
                            Reset to template
                          </button>
                        </div>
                        <textarea
                          name="scope"
                          value={r.scope}
                          onChange={(e) => handleChange(index, e)}
                          placeholder='e.g. {"entities": ["projects"], "format": "csv"}'
                          rows={10}
                          spellCheck={false}
                          required
                        />
                        <p className="der-hint">
                          A JSON object describing which data to export —
                          entities, date range, format, filters, etc.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditMode && (
                  <button
                    type="button"
                    className="der-add-btn"
                    onClick={addRequest}
                  >
                    <span>+</span>
                    Add Another Data Export Request
                  </button>
                )}
              </div>
            </div>

            <div className="der-form-actions">
              <button
                type="button"
                className="der-cancel-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="der-save-btn"
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                  ? "Update Request"
                  : `Submit ${requests.length} Request${
                      requests.length > 1 ? "s" : ""
                    }`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddDataExportRequest;